<template>
  <div class="fin-chart-card">
    <div class="fin-chart-card__header">
      <span class="fin-chart-card__title">{{ cardView === 'chart' ? 'Evolución financiera' : 'Gastos del periodo' }}</span>

      <template v-if="cardView === 'chart'">
        <div class="fin-chart-card__range">
          <button
            v-for="r in RANGES"
            :key="r"
            class="fin-chart-card__pill"
            :class="{ 'fin-chart-card__pill--active': range === r }"
            @click="$emit('update:range', r)"
          >
            {{ r }} meses
          </button>
        </div>

        <div class="fin-chart-card__lines-toggle">
          <button class="fin-chart-card__line-toggle" :class="{ 'fin-chart-card__line-toggle--off': !visible.invoiced }" @click="visible.invoiced = !visible.invoiced">
            <span class="fin-chart-card__dot" style="background:#3b82f6"></span> Ingreso facturado
          </button>
          <button class="fin-chart-card__line-toggle" :class="{ 'fin-chart-card__line-toggle--off': !visible.noi }" @click="visible.noi = !visible.noi">
            <span class="fin-chart-card__dot" style="background:#4338ca"></span> NOI
          </button>
          <button class="fin-chart-card__line-toggle" :class="{ 'fin-chart-card__line-toggle--off': !visible.expenses }" @click="visible.expenses = !visible.expenses">
            <span class="fin-chart-card__dot" style="background:#f87171"></span> Gastos operativos
          </button>
        </div>
      </template>
      <div v-else class="fin-chart-card__spacer"></div>

      <button class="fin-chart-card__view-toggle" @click="cardView = cardView === 'chart' ? 'expenses' : 'chart'">
        <span class="mdi" :class="cardView === 'chart' ? 'mdi-receipt-text-outline' : 'mdi-chart-line'"></span>
        {{ cardView === 'chart' ? 'Ver gastos' : 'Ver gráfica' }}
      </button>
    </div>

    <div v-if="cardView === 'chart'" class="fin-chart-card__canvas-wrap">
      <canvas ref="canvasRef"></canvas>
    </div>

    <div v-else class="fin-chart-card__expenses">
      <div class="fin-chart-card__expenses-body">
        <div class="fin-chart-card__expenses-row fin-chart-card__expenses-row--head">
          <span>Categoría</span>
          <span>Monto</span>
          <span>vs anterior</span>
        </div>
        <div v-for="g in expensesBreakdown?.groups || []" :key="g.key" class="fin-chart-card__expenses-row">
          <span>{{ g.label }}</span>
          <span class="fin-chart-card__expenses-amount">{{ formatCurrency(g.amount) }}</span>
          <span class="fin-chart-card__expenses-diff" :class="g.diff > 0 ? 'fin-chart-card__expenses-diff--up' : 'fin-chart-card__expenses-diff--down'">
            <template v-if="g.diff !== 0">{{ g.diff > 0 ? '▲' : '▼' }} {{ formatCurrency(Math.abs(g.diff)) }}</template>
            <template v-else>—</template>
          </span>
        </div>
        <div class="fin-chart-card__expenses-row fin-chart-card__expenses-row--total">
          <span>Total</span>
          <span class="fin-chart-card__expenses-amount">{{ formatCurrency(expensesBreakdown?.total) }}</span>
          <span></span>
        </div>

        <div v-if="expensesBreakdown?.byPlot?.length" class="fin-chart-card__byplot">
          <span class="fin-chart-card__byplot-title">Gastos cargados a una nave</span>
          <div v-for="p in expensesBreakdown.byPlot" :key="p.plotId" class="fin-chart-card__expenses-row">
            <span>{{ p.plotName }}</span>
            <span class="fin-chart-card__expenses-amount">{{ formatCurrency(p.amount) }}</span>
            <span></span>
          </div>
        </div>

        <div v-if="monthExpenses.length" class="fin-chart-card__list">
          <span class="fin-chart-card__list-title">Gastos capturados a mano ({{ anchorLabel }})</span>
          <div v-for="e in monthExpenses" :key="e.id" class="fin-chart-card__list-row">
            <span class="fin-chart-card__list-name">{{ e.name }}</span>
            <span v-if="e.plot?.name" class="fin-chart-card__list-plot">{{ e.plot.name }}</span>
            <span class="fin-chart-card__list-amount">{{ formatCurrency(e.amount) }}</span>
            <button
              class="fin-chart-card__list-del"
              :class="{ 'fin-chart-card__list-del--confirm': confirmDeleteId === e.id }"
              :title="confirmDeleteId === e.id ? 'Clic otra vez para eliminar' : 'Eliminar gasto'"
              :disabled="deletingId === e.id"
              @click="onDeleteExpense(e.id)"
            >{{ confirmDeleteId === e.id ? '¿Eliminar?' : '✕' }}</button>
          </div>
        </div>

        <button v-if="!isAdding" class="fin-chart-card__add-btn" @click="startAdding">＋ Registrar gasto</button>

        <div v-else class="fin-chart-card__add-form">
          <select v-model="newExpense.category" class="fin-chart-card__add-select">
            <option v-for="cat in CATEGORIES" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
          </select>
          <input v-model="newExpense.name" class="fin-chart-card__add-input" placeholder="Nombre del gasto" />
          <select v-model="newExpense.plotId" class="fin-chart-card__add-select">
            <option value="">Gasto general del parque</option>
            <option v-for="p in plotOptions" :key="p.id" :value="p.id">Nave {{ p.name }}</option>
          </select>
          <span class="fin-chart-card__add-amount-wrap">
            <span>$</span>
            <input v-model.number="newExpense.amount" type="number" class="fin-chart-card__add-amount" placeholder="0" />
          </span>
          <label class="fin-chart-card__add-recurring">
            <input type="checkbox" v-model="newExpense.recurring" /> Recurrente
          </label>
          <div class="fin-chart-card__add-actions">
            <button class="fin-chart-card__add-confirm" @click="confirmAdd">Agregar</button>
            <button class="fin-chart-card__add-cancel" @click="isAdding = false">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler } from 'chart.js'
import { createExpense, getPlotOptions, getExpenses, deleteExpense } from '@/services/expenses'

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler)

const props = defineProps({
  seriesA: { type: Array, default: () => [] },
  seriesB: { type: Array, default: null },
  comparisonMode: { type: Boolean, default: false },
  range: { type: Number, default: 12 },
  anchorYear: { type: Number, default: null },
  anchorMonth: { type: Number, default: null },
  periodType: { type: String, default: 'MONTH' },
  expensesBreakdown: { type: Object, default: null },
  parkId: { type: String, default: '' },
})

const emit = defineEmits(['update:range', 'refresh'])

const RANGES = [6, 12, 24]

const canvasRef = ref(null)
let chartInstance = null

const visible = reactive({ invoiced: true, noi: true, expenses: true })

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(amount)
}

function formatAbbrev(v) {
  const abs = Math.abs(v)
  if (abs >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `$${Math.round(v / 1000)}K`
  return `$${v}`
}

const highlightIndex = computed(() => {
  if (props.periodType !== 'MONTH' || !props.anchorYear || !props.anchorMonth) return -1
  return props.seriesA.findIndex((m) => m.year === props.anchorYear && m.month === props.anchorMonth)
})

// Plugin inline para la línea vertical que marca el periodo seleccionado en el topbar.
const highlightPlugin = {
  id: 'periodHighlight',
  afterDraw(chart) {
    const idx = highlightIndex.value
    if (idx < 0) return
    const meta = chart.getDatasetMeta(0)
    const point = meta?.data?.[idx]
    if (!point) return
    const { ctx, chartArea } = chart
    ctx.save()
    ctx.strokeStyle = '#bfdbfe'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(point.x, chartArea.top)
    ctx.lineTo(point.x, chartArea.bottom)
    ctx.stroke()
    ctx.restore()
  },
}

function buildDatasets() {
  const datasets = []

  if (visible.invoiced) {
    datasets.push({
      label: 'Ingreso facturado',
      data: props.seriesA.map((m) => m.invoicedTotal),
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
      borderWidth: 2,
      pointRadius: 6,
      pointBackgroundColor: '#3b82f6',
      tension: 0.15,
      fill: visible.noi ? '+1' : false,
    })
  }
  if (visible.noi) {
    datasets.push({
      label: 'NOI',
      data: props.seriesA.map((m) => m.noiTotal),
      borderColor: '#4338ca',
      borderWidth: 2.5,
      pointRadius: 8,
      pointBackgroundColor: '#4338ca',
      tension: 0.15,
      fill: false,
    })
  }
  if (visible.expenses) {
    datasets.push({
      label: 'Gastos operativos',
      data: props.seriesA.map((m) => m.expensesTotal),
      borderColor: '#f87171',
      borderWidth: 1.5,
      pointRadius: 6,
      pointBackgroundColor: '#f87171',
      tension: 0.15,
      fill: false,
    })
  }

  if (props.comparisonMode && props.seriesB) {
    if (visible.invoiced) {
      datasets.push({
        label: 'Ingreso facturado (comparado)',
        data: props.seriesB.map((m) => m.invoicedTotal),
        borderColor: 'rgba(59, 130, 246, 0.4)',
        borderWidth: 2,
        borderDash: [5, 4],
        pointRadius: 3,
        pointBackgroundColor: 'rgba(59, 130, 246, 0.4)',
        tension: 0.15,
        fill: false,
      })
    }
    if (visible.noi) {
      datasets.push({
        label: 'NOI (comparado)',
        data: props.seriesB.map((m) => m.noiTotal),
        borderColor: 'rgba(67, 56, 202, 0.4)',
        borderWidth: 2,
        borderDash: [5, 4],
        pointRadius: 3,
        pointBackgroundColor: 'rgba(67, 56, 202, 0.4)',
        tension: 0.15,
        fill: false,
      })
    }
    if (visible.expenses) {
      datasets.push({
        label: 'Gastos operativos (comparado)',
        data: props.seriesB.map((m) => m.expensesTotal),
        borderColor: 'rgba(248, 113, 113, 0.4)',
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 3,
        pointBackgroundColor: 'rgba(248, 113, 113, 0.4)',
        tension: 0.15,
        fill: false,
      })
    }
  }

  return { labels: props.seriesA.map((m) => m.label), datasets }
}

function buildChart() {
  if (!canvasRef.value || !props.seriesA.length) return
  const { labels, datasets } = buildDatasets()

  chartInstance = new Chart(canvasRef.value, {
    type: 'line',
    data: { labels, datasets },
    plugins: [highlightPlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#ffffff',
          titleColor: '#1e3a5f',
          titleFont: { weight: '700', size: 12 },
          bodyColor: '#334155',
          bodyFont: { size: 11 },
          borderColor: '#e2e8f0',
          borderWidth: 1,
          padding: 10,
          usePointStyle: true,
          callbacks: {
            label: (item) => `${item.dataset.label}: ${formatCurrency(item.raw)}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: '#f1f5f9' },
          ticks: { color: '#94a3b8', font: { size: 10, family: getComputedStyle(document.documentElement).getPropertyValue('--font-body').trim() || 'Inter' }, maxTicksLimit: 4, callback: (v) => formatAbbrev(v) },
        },
        x: {
          grid: { display: false },
          ticks: { color: '#94a3b8', font: { size: 11 } },
        },
      },
    },
  })
}

function rebuildChart() {
  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
  buildChart()
}

onMounted(async () => {
  await nextTick()
  buildChart()
})

watch(
  () => [props.seriesA, props.seriesB, props.comparisonMode, props.anchorYear, props.anchorMonth],
  async () => {
    await nextTick()
    rebuildChart()
  },
  { deep: true }
)

watch(visible, () => rebuildChart(), { deep: true })

onBeforeUnmount(() => {
  if (chartInstance) chartInstance.destroy()
})

// --- Vista alterna: gráfica vs. detalle de gastos (reemplaza el espacio de la gráfica, no lo empuja hacia abajo) ---
const cardView = ref('chart')

// El <canvas> se destruye con el v-if al cambiar de vista, así que la instancia de Chart.js queda
// apuntando a un nodo que ya no existe. Al volver a "chart" hay que reconstruirla desde cero; al salir,
// se destruye explícitamente para no dejar una instancia huérfana.
watch(cardView, async (view) => {
  if (view === 'chart') {
    await nextTick()
    rebuildChart()
  } else if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
})

const CATEGORIES = [
  { value: 'UTILITIES', label: 'Servicios comunes' },
  { value: 'MAINTENANCE_CORRECTIVE', label: 'Mantenimiento correctivo' },
  { value: 'SECURITY', label: 'Seguridad perimetral' },
  { value: 'ADMIN', label: 'Administración y honorarios' },
  { value: 'MAINTENANCE_PREVENTIVE', label: 'Mantenimiento preventivo' },
  { value: 'WATER', label: 'Agua' },
  { value: 'WASTE', label: 'Recolección' },
  { value: 'OTHER', label: 'Otros' },
]

// Gastos capturados a mano del mes de referencia, con opción de eliminar los que se agregaron por error.
const monthExpenses = ref([])
const confirmDeleteId = ref('')
const deletingId = ref('')
const anchorKey = computed(() => (props.anchorYear && props.anchorMonth ? `${props.anchorYear}-${String(props.anchorMonth).padStart(2, '0')}` : ''))
const anchorLabel = computed(() => {
  const names = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  return props.anchorMonth ? `${names[props.anchorMonth - 1]} ${props.anchorYear}` : ''
})

async function loadMonthExpenses() {
  if (!props.parkId || !anchorKey.value) { monthExpenses.value = []; return }
  try {
    const result = await getExpenses(props.parkId, anchorKey.value)
    monthExpenses.value = result.expenses || []
  } catch {
    monthExpenses.value = []
  }
}
watch([cardView, anchorKey, () => props.parkId, () => props.expensesBreakdown], () => { if (cardView.value === 'expenses') loadMonthExpenses() })

// Primer clic pide confirmación; el segundo elimina.
async function onDeleteExpense(id) {
  if (confirmDeleteId.value !== id) { confirmDeleteId.value = id; return }
  deletingId.value = id
  try {
    await deleteExpense(id)
    confirmDeleteId.value = ''
    await loadMonthExpenses()
    emit('refresh')
  } finally {
    deletingId.value = ''
  }
}

const isAdding = ref(false)
const newExpense = ref({ category: 'OTHER', name: '', amount: null, recurring: false, plotId: '' })
const plotOptions = ref([])

async function startAdding() {
  isAdding.value = true
  newExpense.value = { category: 'OTHER', name: '', amount: null, recurring: false, plotId: '' }
  try { plotOptions.value = await getPlotOptions(props.parkId) } catch { plotOptions.value = [] }
}

async function confirmAdd() {
  if (!newExpense.value.name || !newExpense.value.amount) return
  const anchor = props.anchorYear && props.anchorMonth ? `${props.anchorYear}-${String(props.anchorMonth).padStart(2, '0')}` : null
  if (!anchor) return
  await createExpense(props.parkId, { ...newExpense.value, plotId: newExpense.value.plotId || null, date: `${anchor}-01` })
  isAdding.value = false
  emit('refresh')
}
</script>

<style scoped>
.fin-chart-card__list { margin-top: 0.6rem; padding-top: 0.5rem; border-top: 1px solid var(--border-active); }
.fin-chart-card__list-title { display: block; font-size: 11px; color: var(--text-muted, #94a3b8); margin-bottom: 0.25rem; }
.fin-chart-card__list-row { display: flex; align-items: center; gap: 0.5rem; font-size: 12px; padding: 0.2rem 0; }
.fin-chart-card__list-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fin-chart-card__list-plot { font-size: 10px; background: rgba(148, 163, 184, 0.2); border-radius: 4px; padding: 0 0.3rem; white-space: nowrap; }
.fin-chart-card__list-amount { font-family: var(--font-mono); white-space: nowrap; }
.fin-chart-card__list-del { background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 12px; padding: 0 0.2rem; }
.fin-chart-card__list-del:hover { color: #dc2626; }
.fin-chart-card__list-del--confirm { color: #dc2626; font-weight: 600; }
.fin-chart-card__byplot { margin-top: 0.6rem; padding-top: 0.5rem; border-top: 1px solid var(--border-active); }
.fin-chart-card__byplot-title { display: block; font-size: 11px; color: var(--text-muted, #94a3b8); margin-bottom: 0.25rem; }

.fin-chart-card {
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.fin-chart-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.fin-chart-card__title {
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.7rem;
  text-transform: uppercase;
  color: #94a3b8;
}

.fin-chart-card__range {
  display: flex;
  gap: 0.3rem;
}

.fin-chart-card__pill {
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #64748b;
  font-family: var(--font-body);
  font-size: 0.65rem;
  padding: 0.2rem 0.55rem;
  cursor: pointer;
}

.fin-chart-card__pill--active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #3b82f6;
  font-weight: 500;
}

.fin-chart-card__lines-toggle {
  display: flex;
  gap: 0.8rem;
  margin-left: auto;
}

.fin-chart-card__spacer {
  flex: 1;
}

.fin-chart-card__view-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 28px;
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #3b82f6;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.72rem;
  padding: 0 0.6rem;
  cursor: pointer;
  flex-shrink: 0;
}

.fin-chart-card__view-toggle:hover {
  background: #eff6ff;
}

.fin-chart-card__line-toggle {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  color: var(--text-content);
  font-family: var(--font-body);
  font-size: 0.68rem;
  cursor: pointer;
  padding: 0;
}

.fin-chart-card__line-toggle--off {
  color: #cbd5e1;
  text-decoration: line-through;
}

.fin-chart-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.fin-chart-card__canvas-wrap {
  flex: 1;
  min-height: 0;
  position: relative;
}

.fin-chart-card__canvas-wrap canvas {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
}

.fin-chart-card__expenses {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.fin-chart-card__expenses::-webkit-scrollbar {
  width: 9px;
}

.fin-chart-card__expenses::-webkit-scrollbar-thumb {
  background: var(--border-subtle);
  border-radius: 3px;
}

.fin-chart-card__expenses::-webkit-scrollbar-track {
  background: transparent;
}

.fin-chart-card__expenses-body {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.2rem 0;
}

.fin-chart-card__expenses-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  font-family: var(--font-body);
  font-size: 0.78rem;
  color: var(--text-content);
}

.fin-chart-card__expenses-row--head {
  color: #94a3b8;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.fin-chart-card__expenses-row--total {
  border-top: 1px solid #f1f5f9;
  padding-top: 0.3rem;
  margin-top: 0.15rem;
  font-weight: 600;
  color: #1e3a5f;
}

.fin-chart-card__expenses-amount {
  font-family: var(--font-mono);
}

.fin-chart-card__expenses-diff {
  font-family: var(--font-mono);
  font-size: 0.72rem;
}

.fin-chart-card__expenses-diff--up {
  color: #e84545;
}

.fin-chart-card__expenses-diff--down {
  color: #10b981;
}

.fin-chart-card__add-btn {
  align-self: flex-start;
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 0.78rem;
  cursor: pointer;
  padding: 0.3rem 0;
}

.fin-chart-card__add-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem;
  background: #f8f9fc;
  border-radius: 6px;
  margin-top: 0.3rem;
}

.fin-chart-card__add-select,
.fin-chart-card__add-input {
  font-family: var(--font-body);
  font-size: 0.78rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  background: #ffffff;
}

.fin-chart-card__add-amount-wrap {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  color: #94a3b8;
}

.fin-chart-card__add-amount {
  border: none;
  outline: none;
  font-family: var(--font-mono);
  width: 70px;
}

.fin-chart-card__add-recurring {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: var(--text-content);
}

.fin-chart-card__add-actions {
  display: flex;
  gap: 0.4rem;
  margin-left: auto;
}

.fin-chart-card__add-confirm {
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 0.75rem;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
}

.fin-chart-card__add-cancel {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  font-family: var(--font-body);
  font-size: 0.75rem;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
}
</style>